import { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';
import TenantList from './components/tenants/TenantList';
import TenantOnboarding from './components/tenants/TenantOnboarding';
import PolicyViewer from './components/policies/PolicyViewer';
import UserManagement from './components/users/UserManagement';
import AlertDashboard from './components/alerts/AlertDashboard';
import RuleManagement from './components/rules/RuleManagement';
import JobQueue from './components/jobs/JobQueue';

function App() {
  const { user } = useAuthenticator((context) => [context.user]);
  const [activeView, setActiveView] = useState('tenants');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('parity:sidebar:collapsed') === 'true';
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('parity:sidebar:collapsed', String(next));
      return next;
    });
  };

  // Theme state — persisted in localStorage keyed per Cognito user so each
  // user's preference is remembered independently on this browser.
  // Falls back to the OS prefers-color-scheme on first visit.
  const userId = user?.username ?? 'default';
  const storageKey = `parity:theme:${userId}`;

  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    // No stored preference — honour the OS setting
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply / remove the 'dark' class on <html> whenever isDark changes.
  // Tailwind's darkMode: 'class' reads this class to activate dark: variants.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem(storageKey, next ? 'dark' : 'light');
  };

  const renderMainContent = () => {
    switch (activeView) {
      case 'tenants':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Tenant Management
              </h2>
              <button
                onClick={() => setShowOnboarding(true)}
                className="btn-primary"
              >
                Add New Tenant
              </button>
            </div>

            {showOnboarding ? (
              <TenantOnboarding onComplete={() => setShowOnboarding(false)} />
            ) : (
              <TenantList />
            )}
          </div>
        );

      case 'policies':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Chrome Policies</h1>
            <PolicyViewer />
          </div>
        );

      case 'alerts':
        return <AlertDashboard onViewChange={setActiveView} />;

      case 'rules':
        return <RuleManagement />;

      case 'jobs':
        return <JobQueue />;

      case 'activity':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Audit Log
            </h2>
            <div className="card">
              <p className="text-gray-600 dark:text-gray-400">
                Audit log viewer coming soon...
              </p>
            </div>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              User Management
            </h2>
            <UserManagement />
          </div>
        );

      case 'changelog':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Change Log
            </h2>
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 space-y-8">

              {/* v1.2 */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Version 1.2</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Bulk Operations &amp; Job Queue</p>

                <div className="mt-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Bulk Alert Resolution</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                      <li>Select individual alerts with per-row checkboxes, or use the header checkbox to select all visible alerts at once</li>
                      <li>Selection action bar shows count of selected alerts and a single-click "Mark N Resolved" button</li>
                      <li>Bulk resolve queues a RESOLVE_ALERTS job, updates each alert in DynamoDB, and soft-archives each in Google Alert Center</li>
                      <li>After triggering bulk resolve, the app automatically navigates to the Jobs screen to show results</li>
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Jobs Screen</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                      <li>Live job queue viewer showing all async operations with status, type, and timing</li>
                      <li>Status summary chips at the top (Completed, Partial Success, Failed, Processing)</li>
                      <li>Real-time progress bar and item counter for in-progress jobs</li>
                      <li>Expandable per-alert result rows showing individual success/failure for bulk operations</li>
                      <li>Auto-refreshes every 10 seconds</li>
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Alert Filtering</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                      <li>New Type filter lets you narrow alerts by alert type (e.g., Gmail phishing, Suspicious login, Sensitive admin action)</li>
                      <li>Type options are built dynamically from the loaded alert data rather than a fixed list</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* v1.1 */}
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Version 1.1</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Security Alerts &amp; Rule Management</p>

                <div className="mt-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Security Alerts Dashboard</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                      <li>Live alert feed sourced from the Google Workspace Alert Center API</li>
                      <li>On-demand Sync Now to pull the latest alerts for all tenants</li>
                      <li>Auto-refresh every 30 seconds in the background</li>
                      <li>Filter alerts by tenant, severity (High / Medium / Low), and status</li>
                      <li>Top Offenders widget surfaces tenants with the most High-severity open alerts</li>
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Rich Alert Detail Panel</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                      <li>Type-specific detail views for phishing, suspicious logins, and sensitive admin actions</li>
                      <li>Phishing alerts show malicious sender, recipient, subject line, and message body snippet</li>
                      <li>Suspicious login alerts show affected account, source IP, and login timestamp</li>
                      <li>Admin action alerts show who performed the action, what was changed, and the target user</li>
                      <li>Direct link to Google's Security Investigation Tool for deeper forensic analysis</li>
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Alert Status Management</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                      <li>Mark alerts as In Review to track active investigations</li>
                      <li>Mark Resolved archives the alert from the Google Alert Center active queue (soft-delete, recoverable for 30 days)</li>
                      <li>Status changes are persisted locally and reflected immediately without a full sync</li>
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Alert Rule Management</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                      <li>Create and manage standardized alert rule profiles</li>
                      <li>Assign rules to specific tenants or apply globally across all tenants</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* v1.0 */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Version 1.0</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Initial Release</p>
                <ul className="mt-3 space-y-1.5 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                  <li>Multi-tenant Google Workspace management</li>
                  <li>Chrome device and user policy viewer</li>
                  <li>Best practice policy comparison with compliance tracking</li>
                  <li>Organizational unit browser via Admin SDK</li>
                  <li>Tenant onboarding with credential verification</li>
                  <li>Cognito user management</li>
                </ul>
              </div>

            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header isDark={isDark} onToggleTheme={toggleTheme} />
      <div className="flex flex-1">
        <Sidebar
          activeView={activeView}
          onViewChange={setActiveView}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebar}
        />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {renderMainContent()}
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
